import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { updateProfile } from "../../../../services/operations/SettingsAPI";
import { User, Mail, Calendar, Phone, FileText, Save, X } from "lucide-react";

const genders = ["Male", "Female", "Non-Binary", "Prefer not to say", "Other"];

export default function EditProfile() {
  const { user } = useSelector((state) => state.profile);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const submitProfileForm = async (data) => {
    try {
      dispatch(updateProfile(data));
    } catch (error) {
      alert(error.response?.data || error.message);
    }
  };

  return (
    <div className="bg-gradient-to-br from-slate-50 via-white to-blue-50/30 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Modern Header */}
        <div className="mb-2">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-xl">
              <User className="h-6 w-6 text-white" />
            </div>
          
          <h1 className="text-2xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent ">
            Edit Profile
          </h1>
          </div>
          <p className="text-slate-600 text-lg">Update your personal information</p>
        </div>

        <form onSubmit={handleSubmit(submitProfileForm)}>
          {/* Profile Information */}
          <div className="relative overflow-hidden mb-8">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-indigo-600/5 rounded-2xl"></div>
            <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-8">
              <div className="flex items-center gap-3 mb-8">
                <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg">
                  <User className="h-6 w-6 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-slate-800">Profile Information</h2>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-slate-700 font-semibold">
                    <User className="h-4 w-4 text-blue-500" />
                    First Name
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    id="firstName"
                    placeholder="Enter first name"
                    className="w-full bg-white/80 backdrop-blur-sm border border-white/30 rounded-xl p-4 text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 shadow-lg transition-all duration-300"
                    {...register("firstName", { required: true })}
                    defaultValue={user?.firstName}
                  />
                  {errors.firstName && (
                    <span className="text-red-500 text-sm">Please enter your first name.</span>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-slate-700 font-semibold">
                    <User className="h-4 w-4 text-emerald-500" />
                    Last Name
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    id="lastName"
                    placeholder="Enter last name"
                    className="w-full bg-white/80 backdrop-blur-sm border border-white/30 rounded-xl p-4 text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 shadow-lg transition-all duration-300"
                    {...register("lastName", { required: true })}
                    defaultValue={user?.lastName}
                  />
                  {errors.lastName && (
                    <span className="text-red-500 text-sm">Please enter your last name.</span>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-slate-700 font-semibold">
                    <Calendar className="h-4 w-4 text-purple-500" />
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    name="dateOfBirth"
                    id="dateOfBirth"
                    className="w-full bg-white/80 backdrop-blur-sm border border-white/30 rounded-xl p-4 text-slate-700 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 shadow-lg transition-all duration-300"
                    {...register("dateOfBirth", {
                      required: {
                        value: true,
                        message: "Please enter your Date of Birth.",
                      },
                      max: {
                        value: new Date().toISOString().split("T")[0],
                        message: "Date of Birth cannot be in the future.",
                      },
                    })}
                    defaultValue={user?.additionalDetails?.dateOfBirth}
                  />
                  {errors.dateOfBirth && (
                    <span className="text-red-500 text-sm">{errors.dateOfBirth.message}</span>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-slate-700 font-semibold">
                    <User className="h-4 w-4 text-indigo-500" />
                    Gender
                  </label>
                  <select
                    name="gender"
                    id="gender"
                    className="w-full bg-white/80 backdrop-blur-sm border border-white/30 rounded-xl p-4 text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 shadow-lg transition-all duration-300"
                    {...register("gender", { required: true })}
                    defaultValue={user?.additionalDetails?.gender}
                  >
                    <option value="">Select Gender</option>
                    {genders.map((ele, i) => (
                      <option key={i} value={ele}>
                        {ele}
                      </option>
                    ))}
                  </select>
                  {errors.gender && (
                    <span className="text-red-500 text-sm">Please select your gender.</span>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-slate-700 font-semibold">
                    <Phone className="h-4 w-4 text-amber-500" />
                    Contact Number
                  </label>
                  <input
                    type="tel"
                    name="contactNumber"
                    id="contactNumber"
                    placeholder="Enter Contact Number"
                    className="w-full bg-white/80 backdrop-blur-sm border border-white/30 rounded-xl p-4 text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500/50 shadow-lg transition-all duration-300"
                    {...register("contactNumber", {
                      required: {
                        value: true,
                        message: "Please enter your Contact Number.",
                      },
                      maxLength: { value: 12, message: "Invalid Contact Number" },
                      minLength: { value: 10, message: "Invalid Contact Number" },
                    })}
                    defaultValue={user?.additionalDetails?.contactNumber}
                  />
                  {errors.contactNumber && (
                    <span className="text-red-500 text-sm">{errors.contactNumber.message}</span>
                  )}
                </div>

                <div className="space-y-2 lg:col-span-2">
                  <label className="flex items-center gap-2 text-slate-700 font-semibold">
                    <FileText className="h-4 w-4 text-pink-500" />
                    About
                  </label>
                  <textarea
                    name="about"
                    id="about"
                    placeholder="Tell us about yourself..."
                    rows={4}
                    className="w-full bg-white/80 backdrop-blur-sm border border-white/30 rounded-xl p-4 text-slate-700 focus:outline-none focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 shadow-lg transition-all duration-300 resize-none"
                    {...register("about", { required: true })}
                    defaultValue={user?.additionalDetails?.about}
                  />
                  {errors.about && (
                    <span className="text-red-500 text-sm">Please enter something about yourself.</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-end mt-8">
            <button
              type="button"
              onClick={() => navigate("/dashboard/my-profile")}
              className="bg-gradient-to-r from-slate-500 to-gray-600 hover:from-slate-600 hover:to-gray-700 text-white py-3 px-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 transform hover:scale-105 font-semibold"
            >
              <X className="h-5 w-5" />
              Cancel
            </button>
            
            <button 
              type="submit" 
              className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white py-3 px-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 transform hover:scale-105 font-semibold"
            >
              <Save className="h-5 w-5" />
              Save Changes
            </button>
          
          </div>
        </form>
        </div>
      </div>
  
  );
}